import React from 'react';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  webpSrc?: string; // Optional: explicitly provide WebP source
  alt: string;
  width?: number;
  height?: number;
  priority?: boolean;
  className?: string;
}

/**
 * OptimizedImage component
 * 
 * Automatically uses WebP format with fallback to original format
 * Supports lazy loading (except when priority=true)
 * Requires explicit width and height for better performance
 * 
 * Note: For Vite imports, you need to import both PNG and WebP versions:
 *   import imagePng from '@/assets/image.png';
 *   import imagePngWebp from '@/assets/image.webp';
 *   import imageWebp from '@/assets/image.webp';
 *   <OptimizedImage src={imagePng} webpSrc={imageWebp} ... />
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  webpSrc,
  alt,
  width,
  height,
  priority = false,
  className = '',
  ...props
}) => {
  // If webpSrc is provided, use it directly
  // Otherwise, try to generate WebP path from original src
  const getWebPSrc = (): string | undefined => {
    if (webpSrc) {
      return webpSrc;
    }
    
    // Try to generate WebP path from original src
    // This works for static paths but not for Vite imports (which have hashes)
    if (typeof src === 'string' && !src.includes('?') && !src.match(/\/assets\/.*-[a-f0-9]+\./)) {
      // Only for non-hashed paths
      return src.replace(/\.(png|jpg|jpeg)$/i, '.webp');
    }
    
    return undefined;
  };

  const webpSource = getWebPSrc();

  return (
    <picture>
      {webpSource && (
        <source
          srcSet={webpSource}
          type="image/webp"
        />
      )}
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        className={className}
        {...props}
      />
    </picture>
  );
};

export default OptimizedImage;

